import React, { useState } from 'react';
import { api, useAuth } from '../App';
import { AgentAvatar } from './AgentAvatars';
import { CheckCircle, ArrowRight, Upload, Users, Rocket, FileText, Building2, Zap, X } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_cc5301f8-029c-4cb1-afaa-ab22368334bb/artifacts/yofpexsn_Logo%20-%20official%20300x300.png';

const STEPS = [
  { id: 'welcome', title: 'Welcome!', icon: Zap, desc: 'Let\'s get your Panzix HR AI Workforce set up in just a few steps.' },
  { id: 'company', title: 'Company Profile', icon: Building2, desc: 'Tell us about your company so AI employees can serve you better.' },
  { id: 'deploy', title: 'Deploy Your First AI Employee', icon: Rocket, desc: 'Choose an AI employee to start automating HR tasks.' },
  { id: 'documents', title: 'Upload Company Documents', icon: FileText, desc: 'Upload handbooks and policies for AI context.' },
  { id: 'team', title: 'Add Team Members', icon: Users, desc: 'Invite your team to collaborate on the platform.' },
  { id: 'done', title: 'You\'re All Set!', icon: CheckCircle, desc: 'Your Panzix HR AI Workforce is ready to go.' },
];

export default function GuidedSetup({ onComplete, onSkip }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [companyForm, setCompanyForm] = useState({ name: '', industry: '', size: '' });
  const [saving, setSaving] = useState(false);

  const current = STEPS[step];
  const progress = ((step) / (STEPS.length - 1)) * 100;

  const handleNext = async () => {
    if (step === 1 && companyForm.name && !user?.company_id) {
      setSaving(true);
      try { await api.post('/api/company/setup', companyForm); } catch(e) { console.error(e); }
      finally { setSaving(false); }
    }
    if (step < STEPS.length - 1) {
      setStep(step + 1);
      try { await api.post('/api/onboarding/update', { current_step: step + 1, steps_done: Array.from({length: step + 1}, (_, i) => i) }); } catch(e) { console.error(e); }
    } else {
      try { await api.post('/api/onboarding/update', { completed: true, current_step: STEPS.length }); } catch(e) { console.error(e); }
      onComplete();
    }
  };

  const handleSkip = async () => {
    try { await api.post('/api/onboarding/update', { completed: true, skipped: true }); } catch(e) { console.error(e); }
    onSkip();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: 580, overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>
        {/* Progress bar */}
        <div style={{ height: 4, background: '#e2e8f0' }}><div style={{ height: '100%', background: 'linear-gradient(90deg, var(--panzix-green), var(--panzix-purple))', width: `${progress}%`, transition: 'width 0.4s ease', borderRadius: 4 }} /></div>
        
        {/* Header */}
        <div style={{ padding: '28px 32px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={LOGO} alt="" style={{ width: 32, height: 32, borderRadius: 8 }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Setup Guide</span>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Step {step + 1} of {STEPS.length}</span>
          </div>
          <button onClick={handleSkip} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} data-testid="skip-setup">
            Skip for now <X size={14} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: 'linear-gradient(135deg, var(--panzix-green), var(--panzix-purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: 'white' }}>
            <current.icon size={28} />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8, color: '#0f172a' }}>{current.title}</h2>
          <p style={{ fontSize: 15, color: '#64748b', marginBottom: 28 }}>{current.desc}</p>

          {/* Step-specific content */}
          {step === 0 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
              {['kathy','sophie','john','omar'].map(a => <AgentAvatar key={a} agentType={a} size={56} />)}
            </div>
          )}

          {step === 1 && !user?.company_id && (
            <div style={{ textAlign: 'left', marginBottom: 16 }}>
              <div className="form-group"><label>Company Name</label><input value={companyForm.name} onChange={e => setCompanyForm(f => ({...f, name: e.target.value}))} placeholder="Acme Corp" data-testid="setup-company-name" /></div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 1 }}><label>Industry</label>
                  <select value={companyForm.industry} onChange={e => setCompanyForm(f => ({...f, industry: e.target.value}))} style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="">Select...</option><option>Technology</option><option>Healthcare</option><option>Finance</option><option>Manufacturing</option><option>Retail</option><option>Education</option><option>Consulting</option><option>Other</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}><label>Size</label>
                  <select value={companyForm.size} onChange={e => setCompanyForm(f => ({...f, size: e.target.value}))} style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="">Select...</option><option>1-10</option><option>11-50</option><option>51-200</option><option>201-500</option><option>500+</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          {step === 1 && user?.company_id && <p style={{ color: '#22c55e', fontWeight: 600, marginBottom: 16 }}>Company already set up!</p>}

          {step === 2 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
              {['kathy','sophie','john','omar','priya','mario','linda','alex'].map(a => (
                <div key={a} style={{ textAlign: 'center', padding: 8 }}><AgentAvatar agentType={a} size={48} /><div style={{ fontSize: 11, fontWeight: 600, marginTop: 4 }}>{a[0].toUpperCase()+a.slice(1)}</div></div>
              ))}
            </div>
          )}

          {step === 3 && <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>You can upload PDF, Word, Excel, PowerPoint, and text files. AI employees will use these for context-aware responses.</p>}
          {step === 4 && <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Invite team members from Settings. Each plan includes seat-based access for your team.</p>}
          {step === 5 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
                {['kathy','sophie','john','omar'].map(a => <AgentAvatar key={a} agentType={a} size={48} />)}
              </div>
              <p style={{ fontWeight: 600, color: '#166534' }}>Your AI team is ready to work!</p>
            </div>
          )}

          <button className="btn-action" onClick={handleNext} disabled={saving} style={{ width: '100%', padding: '14px', fontSize: 15, borderRadius: 12 }} data-testid="setup-next">
            {saving ? 'Saving...' : step === STEPS.length - 1 ? 'Go to Dashboard' : <>Continue <ArrowRight size={16} style={{ marginLeft: 6 }} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
